import React, { type FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	CloseButton,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import { Box, Stack } from '@atlaskit/primitives/compiled';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

const i18n = defineMessages({
	header: {
		id: 'share-and-restrict-dialog.restrict-access-confirmation.header',
		defaultMessage: 'Restrict access',
		description: 'Header for restrict access confirmation modal',
	},
	bodyLineOne: {
		id: 'share-and-restrict-dialog.restrict-access-confirmation.body-line-one',
		defaultMessage:
			'This page will no longer inherit permissions directly from the space and will be accessible for those who are listed in specific access.',
		description: 'First sentence for restrict access confirmation modal body',
	},
	bodyLineTwo: {
		id: 'share-and-restrict-dialog.restrict-access-confirmation.body-line-two',
		defaultMessage:
			'Anyone with Edit access to the page might still be able to restore permissions.',
		description: 'Second sentence for restrict access confirmation modal body',
	},
	cancelButton: {
		id: 'share-and-restrict-dialog.restrict-access-confirmation.cancel',
		defaultMessage: 'Cancel',
		description: 'Cancel button label for restrict access confirmation modal',
	},
	confirmButton: {
		id: 'share-and-restrict-dialog.restrict-access-confirmation.confirm',
		defaultMessage: 'Restrict access',
		description: 'Confirm button label for restrict access confirmation modal',
	},
});

export type RestrictAccessConfirmationDialogProps = {
	onCancel: () => void;
	onConfirm: () => void;
};

export const RestrictAccessConfirmationDialog: FC<RestrictAccessConfirmationDialogProps> = ({
	onCancel,
	onConfirm,
}) => {
	const { Modal } = useChoreographerAssets();

	return (
		<Modal width="small" testId="modal-dialog-restrict-access-confirmation">
			<ModalHeader>
				<ModalTitle>
					<FormattedMessage {...i18n.header} />
				</ModalTitle>
				<CloseButton onClick={onCancel} />
			</ModalHeader>
			<ModalBody>
				<Stack space="space.150">
					<Box>
						<FormattedMessage {...i18n.bodyLineOne} />
					</Box>
					<Box>
						<FormattedMessage {...i18n.bodyLineTwo} />
					</Box>
				</Stack>
			</ModalBody>
			<ModalFooter>
				<Button onClick={onCancel}>
					<FormattedMessage {...i18n.cancelButton} />
				</Button>
				<Button appearance="primary" onClick={onConfirm}>
					<FormattedMessage {...i18n.confirmButton} />
				</Button>
			</ModalFooter>
		</Modal>
	);
};
